import { isPlainObject } from 'lodash-es'
import { action, observable } from 'mobx'
import { type SerializedFilters } from '@/infra/FiltersViewModel'
import { createObjectStorage, type ObjectStorage } from '@/infra/object-storage'
import { type Storage } from '@/infra/storage'
import {
  jobListPerspectives,
  type JobListPerspective,
} from '@/features/jobs/list/job-list-view'

/**
 * A tab as stored, in the viewer's order. Defaults keep only whether they
 * were hidden or deleted, so they pick up later changes.
 */
export interface StoredSavedJobFilter {
  id: string
  name?: string
  filters?: SerializedFilters
  /**
   * The default an own tab starts from.
   */
  baseId?: string
  isHidden?: boolean
  /**
   * Whether a default was deleted. It keeps its place, to come back there.
   */
  isDeleted?: boolean
  /**
   * Whether the tab is being created and isn't saved yet. Drafts are never
   * stored, so one left open when the page goes away doesn't come back.
   */
  isDraft?: boolean
}

/**
 * The viewer's saved filter tabs, per perspective, kept in storage. Shared
 * by every list that shows the tabs, so an edit on one shows on the others.
 */
export class SavedJobFilterStore {
  @observable private accessor _entries: Partial<
    Record<JobListPerspective, StoredSavedJobFilter[]>
  > = {}

  private readonly _storage: ObjectStorage | null

  /**
   * @param storage Where the tabs are kept, or `null` to keep them only
   * until the page reloads.
   */
  constructor(storage: Storage | null) {
    this._storage = storage && createObjectStorage(storage)
    for (const perspective of jobListPerspectives) {
      const stored = this._storage?.getItem(storageKey(perspective))
      if (Array.isArray(stored)) {
        this._entries[perspective] = stored.filter(isStoredSavedJobFilter)
      }
    }
  }

  /**
   * The stored tabs for a perspective, in order.
   */
  entriesFor(perspective: JobListPerspective): StoredSavedJobFilter[] {
    return this._entries[perspective] ?? []
  }

  /**
   * Replaces the stored tabs for a perspective.
   */
  @action.bound
  save(perspective: JobListPerspective, entries: StoredSavedJobFilter[]) {
    this._entries = { ...this._entries, [perspective]: entries }
    this._storage?.setItem(
      storageKey(perspective),
      entries.filter((e) => !e.isDraft),
    )
  }
}

function isStoredSavedJobFilter(value: unknown): value is StoredSavedJobFilter {
  if (!isPlainObject(value)) {
    return false
  }
  const entry = value as Record<string, unknown>
  return (
    typeof entry.id === 'string' &&
    isOptional(entry.name, (v) => typeof v === 'string') &&
    isOptional(entry.filters, isPlainObject) &&
    isOptional(entry.baseId, (v) => typeof v === 'string') &&
    isOptional(entry.isHidden, (v) => typeof v === 'boolean') &&
    isOptional(entry.isDeleted, (v) => typeof v === 'boolean')
  )
}

function isOptional(value: unknown, check: (value: unknown) => boolean) {
  return value === undefined || check(value)
}

function storageKey(perspective: JobListPerspective) {
  return `JobList Tabs - ${perspective}`
}
